import { oc } from "@orpc/contract";
import { z } from "zod";
import { ApprovalDecisionSchema } from "./discounts.contract";

const UUIDSchema = z.string().uuid();

// ---------------------------------------------------------------------------
// Get Order Approval Status
// ---------------------------------------------------------------------------

export const GetOrderApprovalStatusInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
});

export const OrderApprovalDecisionRecordSchema = z.object({
  id: UUIDSchema,
  level: z.number(),
  levelName: z.string().nullable(),
  decision: ApprovalDecisionSchema,
  decidedByUserId: UUIDSchema.nullable(),
  decidedByUserName: z.string().nullable(),
  notes: z.string().nullable(),
  decidedAt: z.coerce.date(),
});

export const GetOrderApprovalStatusResponseSchema = z.object({
  /** Null when no order-level approval exists for this staged PO. */
  orderApprovalId: UUIDSchema.nullable(),
  approvalStatus: z.enum(["PENDING", "APPROVED", "REJECTED"]).nullable(),
  currentLevel: z.number().nullable(),
  requiredLevel: z.number().nullable(),
  matchedRuleName: z.string().nullable(),
  nextLevelName: z.string().nullable(),
  canApproveNextLevel: z.boolean(),
  decisions: z.array(OrderApprovalDecisionRecordSchema),
});

// ---------------------------------------------------------------------------
// Submit Order Approval Decision
// ---------------------------------------------------------------------------

export const SubmitOrderApprovalDecisionInputSchema = z.object({
  orderApprovalId: UUIDSchema,
  decision: ApprovalDecisionSchema,
  notes: z.string().optional(),
  /** Required so the handler can signal the associated workflow to recheck. */
  stagedPurchaseOrderId: UUIDSchema,
});

export const SubmitOrderApprovalDecisionResponseSchema = z.object({
  success: z.boolean(),
  isFullyApproved: z.boolean(),
  currentLevel: z.number(),
  requiredLevel: z.number(),
  approvalStatus: z.enum(["PENDING", "APPROVED", "REJECTED"]),
});

// ---------------------------------------------------------------------------
// Get Draft Gate Status
// ---------------------------------------------------------------------------

export const GetDraftGateStatusInputSchema = z.object({
  stagedPurchaseOrderId: UUIDSchema,
});

const GateStatusSchema = z.enum(["OK", "AWAITING_APPROVAL", "BLOCKED"]);

const GateActorSchema = z.object({
  /** Which approval ladder this actor sits on. */
  system: z.enum(["discount", "order"]),
  level: z.number(),
  name: z.string(),
  /**
   * Indices of the staged-PO lines this actor can act on right now. Populated
   * for the discount gate. Empty for the order-approval gate — approval is
   * whole-order, not per-line.
   */
  actionableLineIndices: z.array(z.number()),
});

const DiscountGateStateSchema = z.object({
  status: GateStatusSchema,
  pendingDiscountCount: z.number(),
  unapprovedLineCount: z.number(),
  issueLineIndices: z.array(z.number()),
  nextActor: GateActorSchema.nullable(),
  description: z.string(),
});

const OrderApprovalGateStateSchema = z.object({
  status: GateStatusSchema,
  matchedRuleName: z.string().nullable(),
  currentApprovalLevel: z.number(),
  requiredApprovalLevel: z.number(),
  approvalRowExists: z.boolean(),
  nextActor: GateActorSchema.nullable(),
  description: z.string(),
});

export const GetDraftGateStatusResponseSchema = z.object({
  discount: DiscountGateStateSchema,
  orderApproval: OrderApprovalGateStateSchema,
  blockingGate: z.enum(["discount", "order_approval"]).nullable(),
});

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export const orderApprovalsContracts = {
  getStatus: oc
    .input(GetOrderApprovalStatusInputSchema)
    .output(GetOrderApprovalStatusResponseSchema)
    .route({ method: "GET", path: "/status" }),
  submitDecision: oc
    .input(SubmitOrderApprovalDecisionInputSchema)
    .output(SubmitOrderApprovalDecisionResponseSchema)
    .route({ method: "POST", path: "/decision" }),
  getDraftGateStatus: oc
    .input(GetDraftGateStatusInputSchema)
    .output(GetDraftGateStatusResponseSchema)
    .route({ method: "GET", path: "/draft-gate-status" }),
} as const;

// ---------------------------------------------------------------------------
// Type exports
// ---------------------------------------------------------------------------

export type GetOrderApprovalStatusInput = z.infer<
  typeof GetOrderApprovalStatusInputSchema
>;
export type GetOrderApprovalStatusResponse = z.infer<
  typeof GetOrderApprovalStatusResponseSchema
>;
export type SubmitOrderApprovalDecisionInput = z.infer<
  typeof SubmitOrderApprovalDecisionInputSchema
>;
export type SubmitOrderApprovalDecisionResponse = z.infer<
  typeof SubmitOrderApprovalDecisionResponseSchema
>;
export type GetDraftGateStatusInput = z.infer<
  typeof GetDraftGateStatusInputSchema
>;
export type GetDraftGateStatusResponse = z.infer<
  typeof GetDraftGateStatusResponseSchema
>;
